// Public media origin: CloudFront when configured, otherwise the S3 bucket hostname.
// Runtime config from GET /api/media/config overrides the build-time env (no rebuild required).
const S3_DEFAULT_BASE = 'https://menubeez.s3.ap-south-1.amazonaws.com';
const CDN_DEFAULT_BASE = 'https://cdn.peppar.in';

declare global {
  interface Window {
    __PEPPAR_MEDIA_BASE__?: string;
  }
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function normalizeMediaBase(value?: string | null): string | null {
  if (!value || !value.trim()) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return null;
  }
  return stripTrailingSlash(trimmed);
}

function envMediaBase(): string | null {
  const mode = (process.env.REACT_APP_MEDIA_CDN || '').trim().toLowerCase();
  if (mode === 's3') {
    return null;
  }
  return normalizeMediaBase(
    process.env.REACT_APP_CLOUDFRONT_URL || process.env.REACT_APP_MEDIA_BASE_URL || CDN_DEFAULT_BASE
  );
}

let runtimeMediaBase: string | null = null;

export function setMediaBaseUrl(url?: string | null): void {
  const normalized = normalizeMediaBase(url);
  runtimeMediaBase = normalized;
  if (typeof window !== 'undefined') {
    window.__PEPPAR_MEDIA_BASE__ = normalized || undefined;
  }
}

export function getMediaBaseUrl(): string {
  if (runtimeMediaBase) {
    return runtimeMediaBase;
  }
  if (typeof window !== 'undefined' && window.__PEPPAR_MEDIA_BASE__) {
    const fromWindow = normalizeMediaBase(window.__PEPPAR_MEDIA_BASE__);
    if (fromWindow) {
      return fromWindow;
    }
  }
  return envMediaBase() || S3_DEFAULT_BASE;
}

export function toPublicMediaUrl(pathOrUrl: string): string {
  const raw = (pathOrUrl || '').trim().replace(/\\/g, '/');
  if (!raw) {
    return raw;
  }
  const base = getMediaBaseUrl();
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      const parsed = new URL(raw);
      const host = parsed.hostname.toLowerCase();
      const baseHost = new URL(base).hostname.toLowerCase();
      const isManagedHost =
        host === baseHost ||
        host === 'cdn.peppar.in' ||
        host.endsWith('.cloudfront.net') ||
        host.includes('amazonaws.com');
      if (!isManagedHost) {
        return raw;
      }
      let key = parsed.pathname.replace(/^\/+/, '');
      if (host.includes('amazonaws.com')) {
        key = key.replace(/^[^/]+\/(?=images\/|logos\/)/, '');
      }
      return key ? `${base}/${key}` : base;
    } catch {
      return raw;
    }
  }
  return `${base}/${raw.replace(/^\/+/, '')}`;
}

/** Inline placeholder — /resources/images/pages/default-user.png is not shipped and 404-loops. */
export const DEFAULT_USER_AVATAR =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">' +
      '<rect width="64" height="64" rx="32" fill="#E9ECEF"/>' +
      '<circle cx="32" cy="24" r="12" fill="#9CA3AF"/>' +
      '<path d="M12 54c3.5-12 14-18 20-18s16.5 6 20 18" fill="#9CA3AF"/>' +
      '</svg>'
  );

export const DEFAULT_IMAGES = {
  get MENU_ITEM() {
    return `${getMediaBaseUrl()}/images/default/menu-default-image.png`;
  },
  get NO_ITEMS_FOUND() {
    return `${getMediaBaseUrl()}/images/default/no-items-found-image.png`;
  },
  get RESTAURANT() {
    return `${getMediaBaseUrl()}/images/default/restaurant-default-image.png`;
  },
  get CAPTAIN_LOGO() {
    return `${getMediaBaseUrl()}/logos/captain-logo.png`;
  },
};

export const IMAGES_BASE_URL = `${S3_DEFAULT_BASE}/images/default`;
export const S3_IMAGES_BASE_URL = S3_DEFAULT_BASE;

export function getCaptainLogoUrl(): string {
  return DEFAULT_IMAGES.CAPTAIN_LOGO;
}

/** @deprecated Use getCaptainLogoUrl() so CloudFront vs S3 follows current config. */
export const CAPTAIN_LOGO_URL = `${S3_DEFAULT_BASE}/logos/captain-logo.png`;

export const brandLogoUrl = (): string => `${getMediaBaseUrl()}/logos/peppar-logo.png`;
export const brandFaviconUrl = (): string => `${getMediaBaseUrl()}/logos/peppar-fabicon.png`;

/**
 * Construct menu item image URL.
 * Pattern: {mediaBase}/images/menu/{restroId}/{menuId}/{filename}
 */
export const getMenuItemImageUrl = (restroId: number, menuId: number, filename: string): string => {
  if (!filename || filename === 'default') {
    return DEFAULT_IMAGES.MENU_ITEM;
  }
  const trimmed = filename.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return toPublicMediaUrl(trimmed);
  }
  if (trimmed.startsWith('images/') || trimmed.startsWith('logos/')) {
    return toPublicMediaUrl(trimmed);
  }
  return `${getMediaBaseUrl()}/images/menu/${restroId}/${menuId}/${trimmed}`;
};

/**
 * Construct restaurant image URL.
 * Pattern: {mediaBase}/images/restaurant/{restroId}/{filename}
 */
export const getRestaurantImageUrl = (restroId: number, filename: string): string => {
  if (!filename || filename === 'default') {
    return DEFAULT_IMAGES.RESTAURANT;
  }
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return toPublicMediaUrl(filename);
  }
  return `${getMediaBaseUrl()}/images/restaurant/${restroId}/${filename}`;
};

/**
 * Resolve restaurant brand logo URL (app logo preferred, then cover/listing image).
 */
export const resolveRestaurantLogoUrl = (
  restaurant: { id: number; appLogo?: string; coverImage?: string }
): string | null => {
  const resolve = (stored?: string): string | null => {
    if (!stored || stored.trim() === '' || stored === 'default') {
      return null;
    }
    const trimmed = stored.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return toPublicMediaUrl(trimmed);
    }
    return getRestaurantImageUrl(restaurant.id, trimmed);
  };
  return resolve(restaurant.appLogo) ?? resolve(restaurant.coverImage);
};

export default DEFAULT_IMAGES;
